import * as React from "react";
import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { WindowArrowUpRegular, DismissRegular } from "@fluentui/react-icons";
import { BRAND } from "../risikoanalyseStep/tokens";
import { useWindowManager } from "./WindowManagerContext";
import { useResponsive } from "../../hooks/useResponsive";
import { WM_DOCK_VAR, computeBottomStack } from "./bottomBars";
import { dropArt } from "../vorgang/detail/todoDrop";

const useStyles = makeStyles({
  // Dock: horizontale Leiste am UNTEREN Rand des WindowLayer-Bounds
  // (NavigationLayout mountet den Layer außerhalb des scrollbaren <main>,
  // daher klebt das Dock fix unten am Content-Bereich). Das CallWidget
  // schwebt mittig und kollidiert räumlich nicht mit diesem unteren,
  // links-bündigen Dock.
  dock: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "flex-start",
    columnGap: "8px",
    rowGap: "8px",
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingLeft: "12px",
    paddingRight: "12px",
    // Dock-Optik: leicht abgesetzter Streifen mit Schatten nach oben.
    backgroundColor: "rgba(45,20,43,0.92)",
    backdropFilter: "blur(6px)",
    ...shorthands.borderTop("1px", "solid", "rgba(255,255,255,0.12)"),
    boxShadow: "0 -6px 18px rgba(45,20,43,0.28)",
    zIndex: 99999,
    pointerEvents: "auto",
  },
  dockPhone: {
    // Auf Phone vollbreit & etwas luftiger (Touch).
    columnGap: "6px",
    paddingTop: "10px",
    paddingBottom: "10px",
  },
  // Ein minimiertes Fenster = ein Chip aus Restore-Fläche + Schließen-Button.
  chip: {
    display: "inline-flex",
    alignItems: "stretch",
    backgroundColor: BRAND.purple,
    color: BRAND.white,
    ...shorthands.border("1px", "solid", "rgba(255,255,255,0.15)"),
    ...shorthands.borderRadius("8px"),
    boxShadow: "0 4px 12px rgba(45,20,43,0.25)",
    overflow: "hidden",
    maxWidth: "260px",
  },
  chipPhone: {
    // Touch: vorgezogenes #10 nicht — aber Chips hier schon angenehm treffbar.
    flexGrow: 1,
    minWidth: "140px",
    maxWidth: "none",
  },
  restoreBtn: {
    appearance: "none",
    cursor: "pointer",
    backgroundColor: "transparent",
    color: "inherit",
    ...shorthands.border("0"),
    display: "inline-flex",
    alignItems: "center",
    columnGap: "10px",
    minWidth: 0,
    paddingTop: "6px",
    paddingBottom: "6px",
    paddingLeft: "12px",
    paddingRight: "8px",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.82rem",
    fontWeight: 600,
    transitionProperty: "background-color, color",
    transitionDuration: "120ms",
    ":hover": {
      backgroundColor: BRAND.gold,
      color: BRAND.ink,
    },
  },
  restoreBtnPhone: {
    flexGrow: 1,
    // Touch-Target ≥44 px (konsistent mit den Fenster-Chrome-Buttons).
    minHeight: "44px",
  },
  pillLabel: {
    flexGrow: 1,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  iconWrap: {
    display: "inline-flex",
    alignItems: "center",
    color: BRAND.gold,
    flexShrink: 0,
  },
  restoreIcon: {
    // Visuelle Affordance: Aufwärtspfeil = "klick stellt wieder her".
    display: "inline-flex",
    alignItems: "center",
    flexShrink: 0,
    fontSize: "18px",
    color: BRAND.gold,
  },
  closeBtn: {
    appearance: "none",
    cursor: "pointer",
    backgroundColor: "transparent",
    color: BRAND.white,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    ...shorthands.borderTop("0"),
    ...shorthands.borderRight("0"),
    ...shorthands.borderBottom("0"),
    ...shorthands.borderLeft("1px", "solid", "rgba(255,255,255,0.15)"),
    paddingTop: "0",
    paddingBottom: "0",
    paddingLeft: "8px",
    paddingRight: "8px",
    fontSize: "16px",
    transitionProperty: "background-color, color",
    transitionDuration: "120ms",
    ":hover": {
      backgroundColor: "rgba(255,255,255,0.16)",
    },
  },
  closeBtnPhone: {
    // Touch-Target ≥44 px in beide Richtungen (Höhe folgt sonst dem Restore-
    // Button via stretch, hier explizit abgesichert).
    minWidth: "44px",
    minHeight: "44px",
  },
});

/** So lange muss ein Zug über einem minimierten Fenster verweilen, bis es aufgeht. */
const FENSTER_OEFFNEN_NACH_MS = 600;

const WindowTaskbar: React.FC = () => {
  const styles = useStyles();
  const { windows, restoreWindow, requestCloseWindow } = useWindowManager();
  const { isPhone } = useResponsive();
  // Ein Fenster im eigenen Browserfenster ist gelöst und steht nicht im Dock
  // (User 27.10.2026); zurück kommt es, indem man es aufs Hauptfenster zieht.
  const minimized = windows.filter((w) => w.state === "minimized" && !w.browserFenster);

  // Dock-Höhe live an `--wm-dock-h` (document root) spiegeln, damit sich alle
  // unteren Leisten (Speicher-Leiste, Risikoanalyse-BottomBar, CallWidget) ÜBER
  // das Dock staffeln statt darunter zu verschwinden. Höhe ist variabel (Umbruch
  // bei vielen Fenstern / Viewport-Resize) → ResizeObserver. Kein Dock → Variable
  // ENTFERNEN (nicht "0px" setzen!), damit die Konsumenten-Fallbacks greifen —
  // insbesondere `<main> padding-bottom: var(--wm-dock-h, 4px)` behält so seine
  // 4px; ein gesetztes "0px" würde den Fallback aushebeln.
  const dockRef = React.useRef<HTMLDivElement | null>(null);
  React.useLayoutEffect(() => {
    const root = document.documentElement;
    const el = dockRef.current;
    if (!el || minimized.length === 0) {
      root.style.removeProperty(WM_DOCK_VAR);
      return;
    }
    const apply = () => {
      const { dockOffset } = computeBottomStack({
        minimizedCount: minimized.length,
        dockHeight: el.getBoundingClientRect().height,
      });
      root.style.setProperty(WM_DOCK_VAR, `${dockOffset}px`);
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => {
      ro.disconnect();
      root.style.removeProperty(WM_DOCK_VAR);
    };
  }, [minimized.length]);

  /**
   * Beim Ziehen über ein minimiertes Fenster verweilen öffnet es (wie in der
   * Windows-Taskleiste) — so lässt sich ein Dokument oder eine Mail aus dem
   * Vorgang auch in ein minimiertes Mailfenster ziehen (User 22.10.2026).
   */
  const ziehTimer = React.useRef<number | null>(null);
  const ziehAbbrechen = React.useCallback(() => {
    if (ziehTimer.current != null) window.clearTimeout(ziehTimer.current);
    ziehTimer.current = null;
  }, []);
  React.useEffect(() => ziehAbbrechen, [ziehAbbrechen]);
  // `dragover` statt `dragenter`: er kommt laufend, solange der Zeiger über
  // dem Knopf steht — auch nachdem ein `dragleave` beim Wechsel auf ein
  // Kind-Element (Symbol, Beschriftung) den Zeitgeber gestoppt hat.
  const ziehOeffnen = (id: string) => (e: React.DragEvent) => {
    if (ziehTimer.current != null) return;
    if (dropArt(Array.from(e.dataTransfer?.types ?? [])) == null) return;
    ziehTimer.current = window.setTimeout(() => {
      ziehTimer.current = null;
      restoreWindow(id);
    }, FENSTER_OEFFNEN_NACH_MS);
  };

  if (minimized.length === 0) return null;

  return (
    <div
      ref={dockRef}
      className={mergeClasses(styles.dock, isPhone && styles.dockPhone)}
      role="toolbar"
      aria-label="Minimierte Fenster"
      // Das Dock sitzt ÜBER der mobilen Bottom-Leiste (Phone), sonst am
      // Content-Boden (Fallback 0 auf Desktop/Tablet, wo keine Leiste existiert).
      style={{ bottom: "var(--mobile-nav-h, 0px)" }}
    >
      {minimized.map((w) => (
        <div key={w.id} className={mergeClasses(styles.chip, isPhone && styles.chipPhone)}>
          <button
            type="button"
            className={mergeClasses(styles.restoreBtn, isPhone && styles.restoreBtnPhone)}
            onClick={() => restoreWindow(w.id)}
            onDragOver={ziehOeffnen(w.id)}
            onDragLeave={(e) => {
              const nach = e.relatedTarget as Node | null;
              if (nach && e.currentTarget.contains(nach)) return;
              ziehAbbrechen();
            }}
            onDrop={ziehAbbrechen}
            title={`${w.title} wiederherstellen`}
          >
            {w.icon && <span className={styles.iconWrap}>{w.icon}</span>}
            <span className={styles.pillLabel}>{w.title}</span>
            <span className={styles.restoreIcon} aria-hidden>
              <WindowArrowUpRegular />
            </span>
          </button>
          <button
            type="button"
            className={mergeClasses(styles.closeBtn, isPhone && styles.closeBtnPhone)}
            // Mit Rückfrage, falls der Inhalt eine hat (Kontaktakte mit
            // ungespeicherten Änderungen) — minimiert ist nicht gespeichert.
            onClick={() => void requestCloseWindow(w.id)}
            title={`${w.title} schließen`}
            aria-label={`${w.title} schließen`}
          >
            <DismissRegular />
          </button>
        </div>
      ))}
    </div>
  );
};

export default WindowTaskbar;
